import 'dayjs/locale/es'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'

import {
  type DailyCarrierMetric,
  type DailySupplierMetric,
  getCarrierQuotaConfigMatrix,
  QUERY_KEYS,
} from '@api'
import { useDashboardFilters } from '@contexts'
import { useMonthlySupplierMetrics } from '@hooks'

import { ChartTitle, DailyMetricsTableWrap } from './styled'
import { MetricsCenteredCell, TruncatedCarrierHeader } from './metricsTableCells'

dayjs.locale('es')

interface TableRow {
  key: string
  supplierLabel: string
  orders: number
  breaches: number
  carrierSlots: number[]
}

interface MonthlySummaryTotals {
  supplierLabel: string
  orders: number
  breaches: number
  carrierSlots: number[]
}

function buildCarrierColumns(
  suppliers: DailySupplierMetric[],
): { carrierId: number; tradeName: string }[] {
  const meta = new Map<number, string>()
  for (const s of suppliers) {
    for (const c of s.carriers ?? []) {
      if (!meta.has(c.carrierId)) {
        meta.set(c.carrierId, c.tradeName?.trim() ?? '')
      }
    }
  }
  return Array.from(meta.entries())
    .map(([carrierId, tradeName]) => ({ carrierId, tradeName }))
    .sort((a, b) => {
      const byName = a.tradeName.localeCompare(b.tradeName, undefined, { sensitivity: 'base' })
      if (byName !== 0) return byName
      return a.carrierId - b.carrierId
    })
}

function confirmedByCarrierId(carriers: DailyCarrierMetric[] | undefined): Map<number, number> {
  const m = new Map<number, number>()
  for (const c of carriers ?? []) {
    m.set(c.carrierId, c.confirmedOrders ?? 0)
  }
  return m
}

function formatMonthYearLabel(d: dayjs.Dayjs) {
  const raw = d.format('MMM YYYY')
  return raw.charAt(0).toLocaleUpperCase('es') + raw.slice(1)
}

const MonthlySupplierMetricsTable = () => {
  const { t } = useTranslation()
  const { selectedMonth } = useDashboardFilters()
  const { data, isLoading } = useMonthlySupplierMetrics()

  const year = dayjs(selectedMonth).year()
  const month = dayjs(selectedMonth).month() + 1

  const { data: quotaData, isLoading: quotaLoading } = useQuery(
    [QUERY_KEYS.CARRIER_QUOTA_CONFIG, year, month],
    () => getCarrierQuotaConfigMatrix(year, month),
    { refetchOnWindowFocus: false },
  )

  const quotaByCarrierId = useMemo(() => {
    const m = new Map<number, number>()
    for (const c of quotaData?.carriers ?? []) {
      m.set(c.carrierId, c.totalMonthlyQuota)
    }
    return m
  }, [quotaData])

  const loading = isLoading || quotaLoading
  const monthLabel = formatMonthYearLabel(dayjs(selectedMonth))

  const { columns, rows, summaryTotals, carrierColumns } = useMemo(() => {
    const suppliers = data?.suppliers ?? []
    const tableCarrierColumns = buildCarrierColumns(suppliers)

    const tableRows: TableRow[] = suppliers.map((s: DailySupplierMetric) => {
      const byId = confirmedByCarrierId(s.carriers)
      return {
        key: `supplier-${s.supplierId}`,
        supplierLabel: s.tradeName,
        orders: s.orders,
        breaches: s.breaches,
        carrierSlots: tableCarrierColumns.map(({ carrierId }) => byId.get(carrierId) ?? 0),
      }
    })

    const monthlySummaryTotals: MonthlySummaryTotals | null =
      tableRows.length > 0
        ? {
            supplierLabel: t('admin_router.dashboard.daily_table_total_orders_row'),
            orders: tableRows.reduce((a, r) => a + r.orders, 0),
            breaches: tableRows.reduce((a, r) => a + r.breaches, 0),
            carrierSlots: tableCarrierColumns.map((_, colIdx) =>
              tableRows.reduce((a, r) => a + (r.carrierSlots[colIdx] ?? 0), 0),
            ),
          }
        : null

    const cols: ColumnsType<TableRow> = [
      {
        title: monthLabel,
        className: 'header-cell-month-label no-separator',
        children: [
          {
            title: t('admin_router.dashboard.daily_table_supplier'),
            dataIndex: 'supplierLabel',
            fixed: 'left',
            width: 200,
            render: (text: string) => <span>{text}</span>,
          },
        ],
      },
      {
        className: 'header-cell-month-label no-border',
        children: [
          {
            title: t('admin_router.dashboard.daily_table_orders'),
            dataIndex: 'orders',
            fixed: 'left',
            align: 'center',
            width: 96,
            render: (value: number) => (
              <span
                style={{
                  color: '#1677ff',
                  fontWeight: 500,
                }}
              >
                {value === 0 ? '-' : value}
              </span>
            ),
          },
          {
            title: t('admin_router.dashboard.daily_table_withdrawal_missing'),
            dataIndex: 'breaches',
            fixed: 'left',
            align: 'center',
            width: 110,
            render: (value: number) => (
              <span
                style={{
                  display: 'inline-block',
                  minWidth: 32,
                  padding: '2px 8px',
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                {value === 0 ? '-' : value}
              </span>
            ),
          },
        ],
      },
      ...tableCarrierColumns.map(({ carrierId, tradeName }, colIdx) => {
        const headerLabel = tradeName || carrierId.toString()
        return {
          className: 'header-cell-month-label',
          title: <TruncatedCarrierHeader label={headerLabel} />,
          align: 'center' as const,
          children: [
            {
              align: 'center' as const,
              width: 60,
              render: (__: unknown, record: TableRow) => (
                <MetricsCenteredCell>
                  {record.carrierSlots[colIdx] === 0 ? '-' : record.carrierSlots[colIdx]}
                </MetricsCenteredCell>
              ),
            },
          ],
        }
      }),
    ]

    return {
      columns: cols,
      rows: tableRows,
      summaryTotals: monthlySummaryTotals,
      carrierColumns: tableCarrierColumns,
    }
  }, [data, t, monthLabel])

  const totalAdjudicado = useMemo(
    () =>
      carrierColumns.reduce((a, { carrierId }) => a + (quotaByCarrierId.get(carrierId) ?? 0), 0),
    [carrierColumns, quotaByCarrierId],
  )

  const totalCamionesPedidos = useMemo(
    () => summaryTotals?.carrierSlots.reduce((a, v) => a + v, 0) ?? 0,
    [summaryTotals],
  )

  const adjVsCumplidoTotal = totalAdjudicado - totalCamionesPedidos

  return (
    <>
      <ChartTitle level={5}>
        {t('admin_router.dashboard.monthly_supplier_table_adj_vs_fulfilled')}
      </ChartTitle>
      <DailyMetricsTableWrap>
        <Table<TableRow>
          loading={loading}
          className="monthly-supplier-metrics-table"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content', y: 43 * 10 }}
          columns={columns}
          dataSource={rows}
          locale={{ emptyText: t('admin_router.dashboard.monthly_supplier_table_empty') }}
          summary={
            summaryTotals
              ? () => (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row>
                      <Table.Summary.Cell
                        index={0}
                        align="left"
                        className="daily-summary-total-cell"
                      >
                        <span style={{ fontWeight: 600 }}>{summaryTotals.supplierLabel}</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={1}
                        align="center"
                        className="daily-summary-total-cell"
                      >
                        <span style={{ color: '#1677ff', fontWeight: 600 }}>
                          {summaryTotals.orders === 0 ? '-' : summaryTotals.orders}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={2}
                        align="center"
                        className="daily-summary-total-cell"
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 32,
                            padding: '2px 8px',
                            borderRadius: 2,
                            fontWeight: 600,
                          }}
                        >
                          {summaryTotals.breaches === 0 ? '-' : summaryTotals.breaches}
                        </span>
                      </Table.Summary.Cell>
                      {summaryTotals.carrierSlots.map((value, colIdx) => (
                        <Table.Summary.Cell
                          key={colIdx}
                          index={3 + colIdx}
                          align="center"
                          className="daily-summary-total-cell"
                        >
                          <MetricsCenteredCell>
                            <span style={{ fontWeight: 600 }}>{value === 0 ? '-' : value}</span>
                          </MetricsCenteredCell>
                        </Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} align="left">
                        <span style={{ fontWeight: 600 }}>
                          {t('admin_router.dashboard.monthly_supplier_table_total_trucks')}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <span style={{ color: '#1677ff', fontWeight: 600 }}>
                          {totalCamionesPedidos === 0 ? '-' : totalCamionesPedidos}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="center">
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 32,
                            padding: '2px 8px',
                            borderRadius: 2,
                            fontWeight: 600,
                          }}
                        >
                          -
                        </span>
                      </Table.Summary.Cell>
                      {summaryTotals.carrierSlots.map((value, colIdx) => (
                        <Table.Summary.Cell key={colIdx} index={3 + colIdx} align="center">
                          <MetricsCenteredCell>
                            <span style={{ fontWeight: 600 }}>-</span>
                          </MetricsCenteredCell>
                        </Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} align="left">
                        <span style={{ fontWeight: 600 }}>
                          {t('admin_router.dashboard.monthly_supplier_table_quota_assigned')}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <span style={{ color: '#1677ff', fontWeight: 600 }}>
                          {totalAdjudicado === 0 ? '-' : totalAdjudicado}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="center">
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 32,
                            padding: '2px 8px',
                            borderRadius: 2,
                            fontWeight: 600,
                          }}
                        >
                          -
                        </span>
                      </Table.Summary.Cell>
                      {summaryTotals.carrierSlots.map((_, colIdx) => {
                        const carrierId = carrierColumns[colIdx]?.carrierId
                        const quota =
                          carrierId !== undefined ? quotaByCarrierId.get(carrierId) ?? 0 : 0
                        return (
                          <Table.Summary.Cell key={colIdx} index={3 + colIdx} align="center">
                            <MetricsCenteredCell>
                              <span style={{ fontWeight: 600 }}>{quota === 0 ? '-' : quota}</span>
                            </MetricsCenteredCell>
                          </Table.Summary.Cell>
                        )
                      })}
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell
                        index={0}
                        align="left"
                        className="daily-summary-total-cell"
                      >
                        <span style={{ fontWeight: 600 }}>
                          {t('admin_router.dashboard.monthly_supplier_table_adj_vs_fulfilled_row')}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={1}
                        align="center"
                        className="daily-summary-total-cell"
                      >
                        <span style={{ color: '#1677ff', fontWeight: 600 }}>
                          {adjVsCumplidoTotal === 0 ? '-' : adjVsCumplidoTotal}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={2}
                        align="center"
                        className="daily-summary-total-cell"
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 32,
                            padding: '2px 8px',
                            borderRadius: 2,
                            fontWeight: 600,
                          }}
                        >
                          -
                        </span>
                      </Table.Summary.Cell>
                      {summaryTotals.carrierSlots.map((slotValue, colIdx) => {
                        const carrierId = carrierColumns[colIdx]?.carrierId
                        const quota =
                          carrierId !== undefined ? quotaByCarrierId.get(carrierId) ?? 0 : 0
                        const diff = quota - slotValue
                        return (
                          <Table.Summary.Cell
                            key={colIdx}
                            index={3 + colIdx}
                            align="center"
                            className="daily-summary-total-cell"
                          >
                            <MetricsCenteredCell>
                              <span style={{ fontWeight: 600 }}>
                                {diff === 0 || diff < 0 ? '-' : diff}
                              </span>
                            </MetricsCenteredCell>
                          </Table.Summary.Cell>
                        )
                      })}
                    </Table.Summary.Row>
                  </Table.Summary>
                )
              : undefined
          }
        />
      </DailyMetricsTableWrap>
    </>
  )
}

export { MonthlySupplierMetricsTable }
